import { useState, useEffect } from "react";
import {
  Container,
  Title,
  Text,
  Flex,
  Image,
  Group,
  Tabs,
  Box,
} from "@mantine/core";
import { IconMapPin, IconCalendar } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import posthog from "posthog-js";

import congrsLogo from "../assets/logo-02.svg";
import eventBG from "../assets/EventViewHero.png";

import {
  useSearchParams,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";
import { useGetPublicEvent, useGetPublicEventLocations } from "../api/event";
import Sponsor from "../components/PublicEvent/Sponsor";
import Speakers from "../components/PublicEvent/Speakers";
import SessionDetails from "../components/PublicEvent/SessionDetails";
import Filters from "../components/PublicEvent/Filters";
import Agenda from "../components/PublicEvent/Agenda";
import LoadingPage from "../components/common/LoadingPage";
import NotReady from "../components/NotReady";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslationService } from "../hooks/useTranslationService";
import { getI18nText } from "../utils/i18nUtils";
import { getTranslatedDay, getTranslatedMonth } from "@congrs/utils";

function PublicEventView() {
  const [activeTab, setActiveTab] = useState("agenda");
  const [selectedSession, setSelectedSession] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const { t, i18n } = useTranslation();
  const { changeLanguage } = useTranslationService();

  //Slug used to look up the event; everything else is keyed on its numeric id
  const navigate = useNavigate();
  const { slug } = useParams();

  const { data: event, isPending, isError } = useGetPublicEvent(slug);
  const eventId = event?.id;

  const searchValue = searchParams.get("search") || "";

  const selectedDate = searchParams.get("dateFilter") || null;

  const locationFilter = searchParams.get("locationFilter")
    ? searchParams.get("locationFilter").split(",")
    : [];

  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  const { data: locations } = useGetPublicEventLocations(eventId);
  const locationOptions =
    locations?.map((loc) => {
      const localizedName = getI18nText(loc.nameI18n, loc.name, i18n);
      return { value: String(loc.id), label: localizedName };
    }) || [];

  const hasActiveFilters =
    selectedDate || locationFilter.length > 0 || searchValue.length > 0;

  const handleSearchChange = (e) => {
    setSearchParams((searchParams) => {
      const v = e.target.value;
      if (!v) searchParams.delete("search");
      else searchParams.set("search", v);
      return searchParams;
    });

    if (event && e.target.value) {
      posthog.capture("public_event_search_used", {
        eventId: event.id,
        eventName: event.name,
        searchQuery: e.target.value,
      });
    }
  };
  const handleChangeDate = (dateId) => {
    setSearchParams((searchParams) => {
      if (!dateId) searchParams.delete("dateFilter");
      else searchParams.set("dateFilter", dateId);
      return searchParams;
    });

    if (event) {
      posthog.capture("public_event_date_filter_changed", {
        eventId: event.id,
        eventName: event.name,
        selectedDateId: dateId,
      });
    }
  };

  const handleChangeLocation = (location) => {
    setSearchParams((searchParams) => {
      if (location.length === 0) {
        searchParams.delete("locationFilter");
        return searchParams;
      }
      searchParams.set("locationFilter", location.join(","));
      return searchParams;
    });

    if (event) {
      posthog.capture("public_event_location_filter_changed", {
        eventId: event.id,
        eventName: event.name,
        selectedLocations: location,
      });
    }
  };

  const clearFilters = () => {
    setSearchParams({});

    if (event) {
      posthog.capture("public_event_filters_cleared", {
        eventId: event.id,
        eventName: event.name,
      });
    }
  };

  if (!slug) {
    navigate("/404");
  }

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setModalOpened(true);

    if (event) {
      posthog.capture("public_event_session_clicked", {
        eventId: event.id,
        eventName: event.name,
        sessionId: session?.id,
        sessionName: session?.name,
      });
    }
  };

  const handleCloseModal = () => {
    setModalOpened(false);
    setSelectedSession(null);
  };

  const sessionParams = {
    eventId,
    locationFilter: locationFilter.join(","),
    searchParams: debouncedSearch,
    ...(selectedDate ? { dateId: selectedDate } : {}),
  };

  const determineLanguageToUse = (event, searchParams) => {
    const urlLang = searchParams.get("lang");
    const storedLang = localStorage.getItem("language");
    const eventLanguages = [
      event.language,
      ...(event.secondaryLanguages || []),
    ];

    if (urlLang && eventLanguages.includes(urlLang)) {
      return urlLang;
    } else if (storedLang && eventLanguages.includes(storedLang)) {
      return storedLang;
    } else if (event.language) {
      return event.language;
    }

    return "en";
  };

  useEffect(() => {
    if (!event) return;

    const languageToUse = determineLanguageToUse(event, searchParams);

    if (i18n.language !== languageToUse) {
      changeLanguage(languageToUse);
    }
  }, [event, searchParams, i18n, changeLanguage]);

  // Track page view when event data is loaded
  useEffect(() => {
    if (event && event.isPublic) {
      posthog.capture("public_event_page_viewed", {
        eventId: event.id,
        eventName: event.name,
      });
    }
  }, [event]);

  // Track tab changes
  useEffect(() => {
    if (event && event.isPublic) {
      posthog.capture("public_event_tab_changed", {
        eventId: event.id,
        eventName: event.name,
        tab: activeTab,
      });
    }
  }, [activeTab, event]);

  //Is event public
  if (!slug || isError || (event && !event.isPublic)) {
    return <Navigate to="/404" replace={true} />;
  }

  if (isPending) {
    return <LoadingPage />;
  }

  if (isError) {
    return null;
  }

  if (!event || !event.isPublic) {
    return null;
  }

  const sortedDates = event?.dates
    ?.slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  if (!sortedDates || sortedDates.length === 0) {
    return <NotReady />;
  }

  const startDate = sortedDates[0]?.date;
  const endDate = sortedDates.at(-1)?.date;

  const dateOptions = sortedDates.map((date) => {
    return {
      value: String(date.id),
      label: `${getTranslatedDay(date.date, t)}, ${getTranslatedMonth(date.date, t)} ${String(new Date(date.date).getDate()).padStart(2, "0")}, ${new Date(date.date).getFullYear()}`,
    };
  });

  return (
    <Box
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Hero Section */}
      <Box
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${eventBG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 10,
          }}
        >
          <LanguageSwitcher event={event} />
        </Box>

        <Container size="lg">
          <Flex direction="column" align="center" gap="md">
            <Image
              src={congrsLogo}
              alt="Logo"
              style={{
                maxHeight: "150px",
                maxWidth: "400px",
                filter: "brightness(0) invert(1)",
              }}
            />
            <Title
              order={1}
              style={{ color: "white", textAlign: "center", fontSize: "3rem" }}
            >
              {getI18nText(event.nameI18n, event.name, i18n)}
            </Title>
            <Group gap="xl" mt="md">
              <Flex align="center" gap="xs">
                <IconCalendar size={20} color="white" />
                <Text size="lg" style={{ color: "white" }}>
                  {getTranslatedMonth(startDate, t)}{" "}
                  {String(new Date(startDate).getDate()).padStart(2, "0")} -{" "}
                  {getTranslatedMonth(endDate, t)}{" "}
                  {String(new Date(endDate).getDate()).padStart(2, "0")},{" "}
                  {new Date(endDate).getFullYear()}
                </Text>
              </Flex>
              {event?.mainLocation && (
                <Flex align="center" gap="xs">
                  <IconMapPin size={20} color="white" />
                  <Text size="lg" style={{ color: "white" }}>
                    {event.mainLocation}
                  </Text>
                </Flex>
              )}
            </Group>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Box
        style={{
          flex: 1,
          backgroundColor: "#f8f9fa",
        }}
      >
        <Container
          size="lg"
          py="xl"
          style={{ flex: 1, justifyContent: "flex-start" }}
        >
          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            variant="pills"
            radius="xl"
            size="xl"
          >
            <Tabs.List style={{ marginBottom: "2rem" }}>
              <Tabs.Tab value="agenda" color="#4175ae">
                {t("agenda")}
              </Tabs.Tab>
              <Tabs.Tab value="speakers" color="#4175ae">
                {t("speakers")}
              </Tabs.Tab>
              <Tabs.Tab value="sponsors" color="#4175ae">
                {t("sponsors")}
              </Tabs.Tab>
            </Tabs.List>

            {/* Agenda Tab */}
            <Tabs.Panel value="agenda">
              <Filters
                hasActiveFilters={hasActiveFilters}
                clearFilters={clearFilters}
                dateOptions={dateOptions}
                selectedDate={selectedDate}
                setSelectedDate={handleChangeDate}
                locationOptions={locationOptions}
                selectedLocations={locationFilter}
                setSelectedLocations={handleChangeLocation}
                searchValue={searchValue}
                handleSearchChange={handleSearchChange}
              />
              <Agenda
                handleSessionClick={handleSessionClick}
                eventId={event.id}
                sessionParams={sessionParams}
                hasActiveFilters={hasActiveFilters}
                clearFilters={clearFilters}
              />
            </Tabs.Panel>

            {/* Speakers Tab */}
            <Tabs.Panel value="speakers">
              <Speakers eventId={event.id} />
            </Tabs.Panel>

            {/* Sponsors Tab */}
            <Tabs.Panel value="sponsors">
              <Sponsor eventId={event.id} />
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        style={{
          backgroundColor: "#4175ae",
          padding: "3rem 0",
          marginTop: "4rem",
        }}
      >
        <Container size="lg">
          <Flex direction="column" align="center" gap="md">
            <Image
              src={congrsLogo}
              alt="Logo"
              style={{
                maxHeight: "100px",
                maxWidth: "250px",
                filter: "brightness(0) invert(1)",
              }}
            />
            <Text style={{ color: "white", textAlign: "center" }}>
              {getI18nText(event.nameI18n, event.name, i18n)}
            </Text>
            <Text size="sm" style={{ color: "#c7ccd1" }}>
              Powered by Congrs
            </Text>
          </Flex>
        </Container>
      </Box>

      {/* Session Detail Modal */}
      <SessionDetails
        modalOpened={modalOpened}
        handleCloseModal={handleCloseModal}
        selectedSession={selectedSession}
      />
    </Box>
  );
}

export default PublicEventView;
